/* ============================================================================
 * เนื้อหาข้อกำหนดการใช้งาน (ไทย / อังกฤษ)
 *
 * แยกออกมาจากหน้า terms_use.js ด้วยเหตุผลเดียวกับ policy_option.js — เป็นข้อความยาว
 * ที่ฝ่ายกฎหมายแก้บ่อยกว่าโครงหน้า และไม่เหมาะจะยัดเป็น key ใน language/*.json
 *
 * ชื่อบริษัท ที่อยู่ และช่องทางติดต่อในเอกสารนี้ ดึงมาจาก "ตั้งค่า › ข้อมูลบริษัท"
 * (GET company ผ่าน services/company.service.js) แก้ที่หลังบ้านแล้วเอกสารเปลี่ยนตาม
 * ไม่ต้องแก้โค้ด — ช่องที่ยังไม่ได้กรอกจึงตกไปใช้ค่าตั้งต้นใน POLICY_COMPANY แทน
 * เพื่อไม่ให้เอกสารมีช่องว่างระหว่างที่ผู้ดูแลยังกรอกข้อมูลไม่ครบ
 *
 * โครงสร้าง block ที่ renderer รองรับ (ชุดเดียวกับหน้านโยบาย + link)
 *   { type: "p",  text: "..." }                     ย่อหน้า
 *   { type: "ul", items: ["...", { label, text }] } รายการ (ใส่ label เพื่อทำหัวข้อตัวหนา)
 *   { type: "note", text: "..." }                   กล่องเน้นข้อความ
 *   { type: "link", href: "/...", text: "..." }     ลิงก์ไปหน้าอื่น (เช่น นโยบายความเป็นส่วนตัว)
 * ========================================================================== */
import { POLICY_COMPANY } from "./policy_option.js";

/* วันที่ปรับปรุงล่าสุด — อัปเดตทุกครั้งที่แก้เนื้อหาด้านล่าง */
export const TERMS_UPDATED = { TH: "9 สิงหาคม 2569", EN: "9 August 2026" };

/**
 * รวมข้อมูลบริษัทจากหลังบ้านกับค่าตั้งต้นในไฟล์นี้ ให้เหลือชุดเดียวที่เอกสารใช้ได้เลย
 *
 * ตัดสินใจเรื่องภาษาไว้ที่นี่ที่เดียว : ฉบับอังกฤษใช้ชื่อภาษาอังกฤษก่อน ถ้าหลังบ้าน
 * กรอกไว้ภาษาเดียวก็ใช้ชื่อที่มี ดีกว่าปล่อยหัวเอกสารว่าง
 *
 * @param {Object} company - ข้อมูลจาก services/company.service.js
 * @param {"TH"|"EN"} lang
 */
export const termsCompany = (company, lang = "TH") => {
  const data = company || {};
  const en = lang === "EN";
  const pick = (value, fallback) => (value || "").trim() || fallback;

  return {
    name: pick(en ? data.name_en || data.name_th : data.name_th || data.name_en, en ? POLICY_COMPANY.name_en : POLICY_COMPANY.name_th),
    // ชื่อระบบบนหัวเอกสารและใน document.title — ใช้ชื่ออังกฤษเป็นหลักเพราะเป็นชื่อแบรนด์
    brand: pick(data.name_en, POLICY_COMPANY.brand),
    address: pick(data.address, en ? POLICY_COMPANY.address_en : POLICY_COMPANY.address_th),
    email: pick(data.email, POLICY_COMPANY.email),
    phone: pick(data.phone, POLICY_COMPANY.phone),
    website: pick(data.website, POLICY_COMPANY.website),
    /* สามอันนี้ไม่มีค่าตั้งต้น — ไม่ได้กรอกก็ไม่ต้องขึ้นบรรทัดในเอกสาร
       ดีกว่าโชว์เบอร์สายด่วนหรือเลขใบอนุญาตปลอมที่ hardcode ไว้ */
    hotline: (data.hotline || "").trim(),
    line_oa: (data.line_oa || "").trim(),
    license_no: (data.license_no || "").trim(),
  };
};

/* ------------------------------------------------------------ เนื้อหาฉบับไทย */
const TH = (c) => ({
  title: "ข้อกำหนดการใช้งาน",
  // ติดข้อมูลบริษัทที่ใช้จริงกลับไปด้วย เผื่อหน้าจอต้องใช้นอกเนื้อหา (ชื่อแท็บ / แถบลิขสิทธิ์)
  company: c,
  subtitle: `ข้อกำหนดฉบับนี้เป็นข้อตกลงระหว่างคุณกับ ${c.name} ในการใช้งานเว็บไซต์และระบบ ${c.brand}`,
  lead: `กรุณาอ่านข้อกำหนดการใช้งานฉบับนี้อย่างละเอียดก่อนใช้เว็บไซต์ ${c.website} และระบบ ${c.brand} ("เว็บไซต์" "บริการ") การเข้าใช้งานถือว่าคุณได้อ่าน เข้าใจ และตกลงผูกพันตามข้อกำหนดนี้แล้ว หากคุณไม่ยอมรับข้อกำหนดข้อใดข้อหนึ่ง กรุณาหยุดใช้งานเว็บไซต์`,
  sections: [
    {
      key: "accept",
      title: "1. การยอมรับข้อกำหนด",
      blocks: [
        {
          type: "p",
          text: "ข้อกำหนดฉบับนี้ใช้กับผู้ใช้งานทุกกลุ่ม ทั้งผู้เข้าชมทั่วไปที่เข้ามาดูประกาศทรัพย์ ผู้ที่ติดต่อสอบถามหรือฝากขาย/ฝากเช่าทรัพย์ และพนักงานหรือนายหน้าที่ได้รับสิทธิ์เข้าใช้ระบบหลังบ้าน",
        },
        {
          type: "p",
          text: "หากคุณใช้งานในนามนิติบุคคล ถือว่าคุณรับรองว่าคุณมีอำนาจผูกพันนิติบุคคลนั้นตามข้อกำหนดฉบับนี้",
        },
        {
          type: "p",
          text: "การใช้งานเว็บไซต์ยังอยู่ภายใต้นโยบายความเป็นส่วนตัวของเราด้วย ซึ่งถือเป็นส่วนหนึ่งของข้อกำหนดฉบับนี้",
        },
      ],
    },
    {
      key: "definition",
      title: "2. คำนิยาม",
      blocks: [
        {
          type: "ul",
          items: [
            { label: "บริษัท / เรา", text: c.name },
            { label: "ผู้ใช้งาน / คุณ", text: "บุคคลที่เข้าชมหรือใช้งานเว็บไซต์และบริการ ไม่ว่าจะสมัครบัญชีหรือไม่ก็ตาม" },
            { label: "ประกาศ", text: "ข้อมูลทรัพย์ที่แสดงบนเว็บไซต์ ประกอบด้วยรายละเอียด ราคา รูปภาพ ตำแหน่งที่ตั้ง และสถานะของทรัพย์" },
            { label: "เนื้อหาของผู้ใช้", text: "ข้อความ รูปภาพ วิดีโอ เอกสาร หรือข้อมูลใด ๆ ที่ผู้ใช้งานส่ง อัปโหลด หรือบันทึกเข้าสู่ระบบ" },
            { label: "เจ้าของทรัพย์", text: "ผู้มีกรรมสิทธิ์ ผู้มีสิทธิครอบครอง หรือผู้ได้รับมอบอำนาจให้เสนอขายหรือให้เช่าทรัพย์นั้น" },
          ],
        },
      ],
    },
    {
      key: "service",
      title: "3. ลักษณะของบริการ",
      highlight: true,
      blocks: [
        {
          type: "p",
          text: "เว็บไซต์ทำหน้าที่เป็นสื่อกลางในการเผยแพร่ประกาศทรัพย์และเชื่อมผู้สนใจเข้ากับเจ้าของทรัพย์หรือนายหน้าที่ดูแลทรัพย์นั้น",
        },
        {
          type: "note",
          text: "บริษัทไม่ใช่คู่สัญญาในการซื้อขายหรือเช่าทรัพย์ระหว่างคุณกับเจ้าของทรัพย์ การเจรจา การทำสัญญา การชำระเงิน และการโอนกรรมสิทธิ์เป็นเรื่องระหว่างคู่สัญญาโดยตรง เว้นแต่กรณีที่บริษัทเข้าเป็นตัวแทนหรือนายหน้าตามสัญญาที่ทำไว้เป็นลายลักษณ์อักษรต่างหาก",
        },
        {
          type: "p",
          text: "เราขอแนะนำให้คุณตรวจสอบเอกสารสิทธิ์ สภาพทรัพย์จริง ภาระผูกพัน และเงื่อนไขทางการเงินด้วยตนเองหรือผ่านผู้เชี่ยวชาญก่อนตัดสินใจทำธุรกรรมทุกครั้ง",
        },
        {
          type: "p",
          text: "บริษัทอาจเพิ่ม ปรับปรุง จำกัด หรือยกเลิกฟีเจอร์ใด ๆ ของเว็บไซต์ได้ตามความเหมาะสม รวมถึงอาจหยุดให้บริการชั่วคราวเพื่อปรับปรุงระบบ โดยจะพยายามแจ้งล่วงหน้าเท่าที่ทำได้",
        },
      ],
    },
    {
      key: "account",
      title: "4. บัญชีผู้ใช้งาน",
      blocks: [
        {
          type: "p",
          text: "การเข้าชมประกาศทรัพย์บนเว็บไซต์สาธารณะไม่จำเป็นต้องมีบัญชีผู้ใช้ ส่วนระบบบริหารจัดการภายในเปิดให้เฉพาะพนักงานและนายหน้าที่ได้รับสิทธิ์เท่านั้น",
        },
        {
          type: "ul",
          items: [
            "คุณต้องให้ข้อมูลที่ถูกต้องและเป็นปัจจุบันในการสมัครหรือใช้งานบัญชี",
            "คุณมีหน้าที่รักษารหัสผ่านและข้อมูลการเข้าสู่ระบบไว้เป็นความลับ และรับผิดชอบต่อการกระทำทั้งหมดที่เกิดขึ้นภายใต้บัญชีของคุณ",
            "ห้ามให้ผู้อื่นใช้บัญชีของคุณ และห้ามใช้บัญชีของผู้อื่นโดยไม่ได้รับอนุญาต",
            "หากพบว่าบัญชีถูกเข้าถึงโดยไม่ได้รับอนุญาต ต้องแจ้งบริษัททันทีตามช่องทางในข้อ 18",
            "สิทธิ์การเข้าถึงข้อมูลในระบบเป็นไปตามบทบาท (role) ที่ผู้ดูแลกำหนด ห้ามพยายามเข้าถึงข้อมูลนอกเหนือสิทธิ์ที่ได้รับ",
          ],
        },
      ],
    },
    {
      key: "listing",
      title: "5. การลงประกาศและเนื้อหาของผู้ใช้",
      blocks: [
        {
          type: "p",
          text: "เมื่อคุณลงประกาศทรัพย์หรือส่งเนื้อหาใด ๆ เข้าสู่ระบบ คุณรับรองและรับประกันว่า",
        },
        {
          type: "ul",
          items: [
            "คุณเป็นเจ้าของทรัพย์ หรือได้รับมอบอำนาจจากเจ้าของทรัพย์ให้เสนอขายหรือให้เช่าทรัพย์นั้นแล้ว",
            "ข้อมูลทรัพย์ ราคา ขนาดพื้นที่ ตำแหน่งที่ตั้ง และสถานะที่ระบุไว้ถูกต้องตรงตามความเป็นจริง",
            "รูปภาพและวิดีโอเป็นภาพของทรัพย์นั้นจริง และคุณมีสิทธิ์ใช้งานหรือได้รับอนุญาตจากเจ้าของลิขสิทธิ์แล้ว",
            "เนื้อหาไม่ละเมิดสิทธิ์ของบุคคลอื่น ไม่ผิดกฎหมาย ไม่หมิ่นประมาท และไม่ก่อให้เกิดความเข้าใจผิด",
            "หากเนื้อหามีข้อมูลส่วนบุคคลของผู้อื่น เช่น ข้อมูลลูกค้าหรือเจ้าของทรัพย์ คุณได้รับความยินยอมหรือมีฐานทางกฎหมายรองรับแล้ว",
          ],
        },
        {
          type: "p",
          text: "คุณยังคงเป็นเจ้าของสิทธิ์ในเนื้อหาที่ส่งเข้ามา แต่คุณอนุญาตให้บริษัทใช้ ทำสำเนา ปรับขนาด ใส่ตราสัญลักษณ์ และเผยแพร่เนื้อหานั้นบนเว็บไซต์และช่องทางการตลาดของบริษัท เพื่อวัตถุประสงค์ในการประชาสัมพันธ์ทรัพย์ ตลอดระยะเวลาที่ประกาศยังเผยแพร่อยู่",
        },
        {
          type: "p",
          text: "บริษัทมีสิทธิ์ตรวจสอบ แก้ไขรูปแบบ ระงับ หรือลบประกาศและเนื้อหาที่ไม่เป็นไปตามข้อกำหนดนี้ได้ โดยไม่ต้องแจ้งให้ทราบล่วงหน้า",
        },
      ],
    },
    {
      key: "accuracy",
      title: "6. ความถูกต้องของข้อมูลที่แสดงบนเว็บไซต์",
      blocks: [
        {
          type: "p",
          text: "ข้อมูลประกาศส่วนใหญ่มาจากเจ้าของทรัพย์และนายหน้า เราตรวจสอบตามสมควรแต่ไม่อาจรับประกันความถูกต้อง ครบถ้วน หรือความเป็นปัจจุบันของข้อมูลทุกรายการได้",
        },
        {
          type: "ul",
          items: [
            "ราคาที่แสดงเป็นราคาเสนอเบื้องต้น อาจเปลี่ยนแปลงได้โดยไม่ต้องแจ้งล่วงหน้า และไม่ถือเป็นคำเสนอที่ผูกพันตามกฎหมาย",
            "สถานะทรัพย์ (ว่าง / จอง / ขายแล้ว / ปล่อยเช่าแล้ว) อาจไม่ตรงกับสถานะจริงในขณะที่คุณเข้าชม",
            "รูปภาพ ภาพจำลอง แผนผัง และพื้นที่ใช้สอยเป็นข้อมูลประกอบการตัดสินใจเท่านั้น อาจต่างจากสภาพจริง",
            "ข้อมูลสิ่งอำนวยความสะดวก ระยะทางไปยังรถไฟฟ้าหรือสถานที่ใกล้เคียง เป็นค่าประมาณ",
          ],
        },
        {
          type: "p",
          text: "หากคุณพบข้อมูลที่ไม่ถูกต้องหรือประกาศที่น่าสงสัย โปรดแจ้งเราตามช่องทางในข้อ 18 เพื่อให้เราตรวจสอบและแก้ไข",
        },
      ],
    },
    {
      key: "prohibited",
      title: "7. ข้อห้ามในการใช้งาน",
      blocks: [
        { type: "p", text: "ในการใช้งานเว็บไซต์และบริการ คุณตกลงว่าจะไม่กระทำการดังต่อไปนี้" },
        {
          type: "ul",
          items: [
            "ลงประกาศทรัพย์ที่ไม่มีอยู่จริง ประกาศซ้ำซ้อนเกินสมควร หรือใช้ราคาที่ไม่เป็นความจริงเพื่อล่อให้ติดต่อ",
            "ใช้เว็บไซต์เพื่อการฉ้อโกง ฟอกเงิน หรือการกระทำที่ผิดกฎหมายอื่นใด",
            "เก็บรวบรวมข้อมูลผู้ใช้งานหรือข้อมูลประกาศด้วยโปรแกรมอัตโนมัติ (scraping, bot, crawler) โดยไม่ได้รับอนุญาตเป็นลายลักษณ์อักษร",
            "พยายามเจาะระบบ ทดสอบช่องโหว่ ข้ามการยืนยันตัวตน หรือเข้าถึงข้อมูลที่ไม่ได้รับสิทธิ์",
            "อัปโหลดไวรัส มัลแวร์ หรือโค้ดที่อาจทำให้ระบบเสียหาย",
            "ส่งข้อความโฆษณาหรือสแปมไปยังผู้ใช้งานรายอื่น",
            "ทำซ้ำ ดัดแปลง หรือนำข้อมูลประกาศไปเผยแพร่ในเชิงพาณิชย์บนช่องทางอื่นโดยไม่ได้รับอนุญาต",
            "แอบอ้างเป็นบุคคลอื่น หรือแสดงตนว่าเกี่ยวข้องกับบริษัทโดยไม่เป็นความจริง",
          ],
        },
        {
          type: "p",
          text: "การฝ่าฝืนข้อห้ามข้างต้น บริษัทมีสิทธิ์ระงับการใช้งานทันที และดำเนินการตามกฎหมายตามที่เห็นสมควร",
        },
      ],
    },
    {
      key: "ip",
      title: "8. ทรัพย์สินทางปัญญา",
      blocks: [
        {
          type: "p",
          text: `ชื่อทางการค้า เครื่องหมายการค้า โลโก้ ${c.brand} ส่วนออกแบบหน้าเว็บ ซอร์สโค้ด ฐานข้อมูล และเนื้อหาที่บริษัทจัดทำขึ้น เป็นทรัพย์สินทางปัญญาของบริษัทหรือผู้อนุญาตให้ใช้สิทธิ์`,
        },
        {
          type: "p",
          text: "คุณใช้เว็บไซต์ได้เพื่อวัตถุประสงค์ส่วนตัวหรือเพื่อการปฏิบัติงานตามสิทธิ์ที่ได้รับเท่านั้น ห้ามทำซ้ำ ดัดแปลง แจกจ่าย ขาย หรือทำวิศวกรรมย้อนกลับส่วนใดของบริการโดยไม่ได้รับอนุญาตเป็นลายลักษณ์อักษร",
        },
      ],
    },
    {
      key: "third_party",
      title: "9. บริการและลิงก์ของบุคคลภายนอก",
      blocks: [
        {
          type: "p",
          text: "เว็บไซต์อาจมีลิงก์ไปยังเว็บไซต์อื่น หรือใช้บริการของบุคคลภายนอก เช่น แผนที่ ช่องทางโซเชียลมีเดีย และบริการยืนยันตัวตน",
        },
        {
          type: "p",
          text: "เนื้อหาและการให้บริการของบุคคลภายนอกอยู่นอกเหนือการควบคุมของเรา บริษัทไม่รับผิดชอบต่อความเสียหายที่เกิดจากการใช้บริการเหล่านั้น และแนะนำให้คุณอ่านข้อกำหนดและนโยบายของผู้ให้บริการรายนั้นเพิ่มเติม",
        },
      ],
    },
    {
      key: "fee",
      title: "10. ค่าบริการและค่านายหน้า",
      blocks: [
        {
          type: "p",
          text: "การเข้าชมประกาศทรัพย์บนเว็บไซต์ไม่มีค่าใช้จ่าย ส่วนบริการที่มีค่าใช้จ่าย เช่น การโปรโมตประกาศหรือบริการนายหน้า จะแจ้งเงื่อนไขและอัตราค่าบริการให้ทราบก่อนเริ่มให้บริการทุกครั้ง",
        },
        {
          type: "p",
          text: "ค่านายหน้าหรือค่าตอบแทนอื่นเป็นไปตามสัญญาที่ตกลงกันเป็นรายกรณี บริษัทไม่เรียกเก็บเงินจากผู้เข้าชมเว็บไซต์เพื่อแลกกับการติดต่อเจ้าของทรัพย์",
        },
        {
          type: "note",
          text: "บริษัทไม่มีนโยบายเรียกเก็บเงินมัดจำผ่านช่องทางส่วนตัวของพนักงาน หากมีผู้อ้างชื่อบริษัทเพื่อขอให้โอนเงิน กรุณาตรวจสอบกับเราตามช่องทางในข้อ 18 ก่อนดำเนินการ",
        },
      ],
    },
    {
      key: "local",
      title: "11. รายการโปรดและข้อมูลที่เก็บในเครื่องของคุณ",
      blocks: [
        {
          type: "p",
          text: "รายการทรัพย์ที่คุณกดถูกใจ (รายการโปรด) และการตั้งค่าบางอย่าง เช่น ภาษาที่เลือก ถูกเก็บไว้ในเบราว์เซอร์ของคุณเอง ไม่ได้ผูกกับบัญชีผู้ใช้",
        },
        {
          type: "p",
          text: "ข้อมูลดังกล่าวจะหายไปหากคุณล้างข้อมูลเบราว์เซอร์ ใช้โหมดไม่ระบุตัวตน หรือเปลี่ยนอุปกรณ์ บริษัทไม่สามารถกู้คืนรายการโปรดให้ได้",
        },
      ],
    },
    {
      key: "privacy",
      title: "12. ข้อมูลส่วนบุคคล",
      blocks: [
        {
          type: "p",
          text: "การเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลของคุณเป็นไปตามนโยบายความเป็นส่วนตัวของบริษัท ซึ่งจัดทำขึ้นตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA)",
        },
        { type: "link", href: "/policy-privacy", text: "อ่านนโยบายความเป็นส่วนตัว" },
      ],
    },
    {
      key: "terminate",
      title: "13. การระงับหรือยกเลิกการให้บริการ",
      blocks: [
        {
          type: "p",
          text: "บริษัทมีสิทธิ์ระงับหรือยกเลิกสิทธิ์การเข้าใช้งานของคุณ ลบประกาศ หรือปิดบัญชี ในกรณีต่อไปนี้",
        },
        {
          type: "ul",
          items: [
            "คุณฝ่าฝืนข้อกำหนดฉบับนี้หรือกฎหมายที่เกี่ยวข้อง",
            "ข้อมูลที่ให้ไว้ไม่เป็นความจริงหรือก่อให้เกิดความเสียหายแก่ผู้อื่น",
            "คุณสิ้นสุดความสัมพันธ์ในฐานะพนักงานหรือนายหน้าของบริษัท",
            "มีเหตุจำเป็นตามกฎหมายหรือคำสั่งของหน่วยงานที่มีอำนาจ",
          ],
        },
        {
          type: "p",
          text: "คุณสามารถขอยกเลิกบัญชีได้ตลอดเวลาโดยแจ้งมาตามช่องทางในข้อ 18 ทั้งนี้ ข้อกำหนดที่ตามสภาพต้องมีผลต่อไป เช่น ข้อจำกัดความรับผิดและทรัพย์สินทางปัญญา ยังคงมีผลผูกพันหลังการยกเลิก",
        },
      ],
    },
    {
      key: "liability",
      title: "14. ข้อจำกัดความรับผิด",
      blocks: [
        {
          type: "p",
          text: "เราให้บริการเว็บไซต์ตามสภาพที่เป็นอยู่ (as is) และจะพยายามอย่างเต็มความสามารถให้ระบบใช้งานได้ต่อเนื่องและข้อมูลถูกต้อง แต่ไม่รับประกันว่าเว็บไซต์จะไม่มีข้อผิดพลาดหรือใช้งานได้ตลอดเวลาโดยไม่หยุดชะงัก",
        },
        {
          type: "p",
          text: "เท่าที่กฎหมายอนุญาต บริษัทไม่รับผิดต่อความเสียหายที่เกิดจาก",
        },
        {
          type: "ul",
          items: [
            "การตัดสินใจซื้อ ขาย เช่า หรือลงทุนของคุณโดยอาศัยข้อมูลบนเว็บไซต์",
            "ข้อพิพาท การผิดสัญญา หรือการกระทำของเจ้าของทรัพย์ ผู้เช่า ผู้ซื้อ หรือบุคคลภายนอก",
            "การหยุดชะงักของระบบ ข้อมูลสูญหาย หรือเหตุสุดวิสัยที่อยู่นอกเหนือการควบคุมของบริษัท",
            "การเข้าถึงบัญชีของคุณโดยบุคคลอื่น อันเกิดจากการที่คุณไม่รักษาข้อมูลการเข้าสู่ระบบไว้เป็นความลับ",
          ],
        },
        {
          type: "p",
          text: "ข้อจำกัดข้างต้นไม่ตัดสิทธิ์ความรับผิดที่กฎหมายกำหนดให้ไม่อาจจำกัดได้ เช่น ความรับผิดที่เกิดจากการจงใจหรือประมาทเลินเล่ออย่างร้ายแรงของบริษัท",
        },
      ],
    },
    {
      key: "indemnity",
      title: "15. การชดใช้ค่าเสียหาย",
      blocks: [
        {
          type: "p",
          text: "หากบุคคลภายนอกเรียกร้องค่าเสียหายจากบริษัทอันเนื่องมาจากเนื้อหาที่คุณส่งเข้าระบบ การใช้งานที่ฝ่าฝืนข้อกำหนดฉบับนี้ หรือการละเมิดสิทธิ์ของผู้อื่นโดยคุณ คุณตกลงชดใช้ค่าเสียหาย ค่าปรับ และค่าใช้จ่ายตามสมควรที่บริษัทต้องรับภาระ รวมถึงค่าทนายความ",
        },
      ],
    },
    {
      key: "changes",
      title: "16. การเปลี่ยนแปลงข้อกำหนด",
      blocks: [
        {
          type: "p",
          text: "บริษัทอาจปรับปรุงข้อกำหนดฉบับนี้เป็นครั้งคราว โดยจะประกาศฉบับใหม่บนหน้านี้พร้อมระบุวันที่ปรับปรุงล่าสุด การใช้งานเว็บไซต์ต่อไปหลังการประกาศ ถือว่าคุณยอมรับข้อกำหนดฉบับที่แก้ไขแล้ว",
        },
        {
          type: "p",
          text: "หากเป็นการเปลี่ยนแปลงในสาระสำคัญที่กระทบสิทธิ์ของผู้ใช้งานที่มีบัญชี เราจะแจ้งให้ทราบล่วงหน้าทางอีเมลหรือการแจ้งเตือนในระบบ",
        },
      ],
    },
    {
      key: "law",
      title: "17. กฎหมายที่ใช้บังคับและการระงับข้อพิพาท",
      blocks: [
        {
          type: "p",
          text: "ข้อกำหนดฉบับนี้อยู่ภายใต้บังคับและตีความตามกฎหมายไทย ข้อพิพาทที่เกิดจากหรือเกี่ยวข้องกับการใช้งานเว็บไซต์ให้อยู่ในเขตอำนาจของศาลไทย",
        },
        {
          type: "p",
          text: "ก่อนดำเนินคดี เราขอให้คุณติดต่อบริษัทเพื่อหาทางยุติข้อพิพาทด้วยความเข้าใจอันดีก่อน",
        },
        {
          type: "p",
          text: "หากข้อกำหนดข้อใดตกเป็นโมฆะหรือไม่อาจบังคับใช้ได้ ให้ข้อกำหนดข้ออื่นยังคงมีผลสมบูรณ์ต่อไป",
        },
      ],
    },
    {
      key: "contact",
      title: "18. ติดต่อเรา",
      blocks: [
        { type: "p", text: "หากมีคำถามเกี่ยวกับข้อกำหนดฉบับนี้ หรือต้องการแจ้งปัญหาการใช้งาน ติดต่อเราได้ที่" },
        {
          type: "ul",
          /* ช่องทางที่ผู้ดูแลยังไม่ได้กรอกจะไม่ขึ้นเป็นบรรทัดว่าง (ดู termsCompany) */
          items: [
            { label: "ผู้ให้บริการ", text: c.name },
            { label: "ที่อยู่", text: c.address },
            { label: "อีเมล", text: c.email },
            { label: "โทรศัพท์", text: c.phone },
            ...(c.hotline ? [{ label: "สายด่วน", text: c.hotline }] : []),
            ...(c.line_oa ? [{ label: "LINE", text: c.line_oa }] : []),
            { label: "เว็บไซต์", text: c.website },
          ],
        },
      ],
    },
  ],
});

/* -------------------------------------------------------- เนื้อหาฉบับอังกฤษ */
const EN = (c) => ({
  title: "Terms of Use",
  company: c,
  subtitle: `These terms form the agreement between you and ${c.name} for the use of the ${c.brand} website and system.`,
  lead: `Please read these Terms of Use carefully before using ${c.website} and the ${c.brand} system (the "website", the "service"). By accessing the website you confirm that you have read, understood and agreed to be bound by these terms. If you do not accept any part of them, please stop using the website.`,
  sections: [
    {
      key: "accept",
      title: "1. Acceptance of These Terms",
      blocks: [
        {
          type: "p",
          text: "These terms apply to every user: visitors browsing property listings, people who enquire about or list a property with us, and staff or agents granted access to the internal system.",
        },
        {
          type: "p",
          text: "If you use the service on behalf of an organisation, you represent that you are authorised to bind that organisation to these terms.",
        },
        {
          type: "p",
          text: "Your use of the website is also governed by our Privacy Policy, which forms part of these terms.",
        },
      ],
    },
    {
      key: "definition",
      title: "2. Definitions",
      blocks: [
        {
          type: "ul",
          items: [
            { label: "The Company / we", text: c.name },
            { label: "User / you", text: "Any person who visits or uses the website and service, whether or not they hold an account." },
            { label: "Listing", text: "Property information published on the website, including details, price, images, location and status." },
            { label: "User content", text: "Any text, image, video, document or data submitted, uploaded or recorded into the system by a user." },
            { label: "Property owner", text: "The owner, lawful possessor, or a person authorised to offer the property for sale or rent." },
          ],
        },
      ],
    },
    {
      key: "service",
      title: "3. Nature of the Service",
      highlight: true,
      blocks: [
        {
          type: "p",
          text: "The website acts as an intermediary that publishes property listings and connects interested parties with property owners or the agents handling those properties.",
        },
        {
          type: "note",
          text: "The Company is not a party to any sale or lease between you and a property owner. Negotiation, contracting, payment and transfer of title are matters directly between the contracting parties, except where the Company acts as agent or broker under a separate written agreement.",
        },
        {
          type: "p",
          text: "We recommend that you verify title documents, the actual condition of the property, encumbrances and financial terms yourself or through a professional adviser before entering into any transaction.",
        },
        {
          type: "p",
          text: "The Company may add, change, restrict or discontinue any feature of the website, and may suspend the service temporarily for maintenance, giving advance notice where reasonably possible.",
        },
      ],
    },
    {
      key: "account",
      title: "4. User Accounts",
      blocks: [
        {
          type: "p",
          text: "No account is required to browse listings on the public website. The internal management system is available only to authorised staff and agents.",
        },
        {
          type: "ul",
          items: [
            "You must provide accurate, up-to-date information when registering or using an account.",
            "You are responsible for keeping your password and sign-in details confidential, and for all activity carried out under your account.",
            "You must not let anyone else use your account, or use another person's account without permission.",
            "If you believe your account has been accessed without authorisation, notify us immediately using the details in section 18.",
            "Access to data is governed by the role permissions set by your administrator. Do not attempt to access data beyond the permissions granted to you.",
          ],
        },
      ],
    },
    {
      key: "listing",
      title: "5. Listings and User Content",
      blocks: [
        { type: "p", text: "When you publish a listing or submit any content to the system, you represent and warrant that:" },
        {
          type: "ul",
          items: [
            "You own the property, or have been authorised by the owner to offer it for sale or rent.",
            "The property details, price, area, location and status stated are accurate and truthful.",
            "The images and videos genuinely depict that property and you hold the rights or permission to use them.",
            "The content does not infringe anyone's rights, is not unlawful or defamatory, and is not misleading.",
            "Where the content contains another person's personal data, such as customer or owner records, you have obtained consent or have another lawful basis.",
          ],
        },
        {
          type: "p",
          text: "You retain ownership of the content you submit, but you grant the Company the right to use, copy, resize, watermark and publish it on the website and the Company's marketing channels for the purpose of promoting the property, for as long as the listing remains published.",
        },
        {
          type: "p",
          text: "The Company may review, reformat, suspend or remove any listing or content that does not comply with these terms, without prior notice.",
        },
      ],
    },
    {
      key: "accuracy",
      title: "6. Accuracy of Published Information",
      blocks: [
        {
          type: "p",
          text: "Most listing information comes from property owners and agents. We check it as far as is reasonable, but we cannot guarantee that every record is accurate, complete or current.",
        },
        {
          type: "ul",
          items: [
            "Prices shown are indicative, may change without notice, and do not constitute a binding offer.",
            "Property status (available, reserved, sold, rented) may not reflect the actual status at the time you view it.",
            "Photographs, renderings, floor plans and usable areas are provided as a guide and may differ from the property as built.",
            "Facilities, and distances to transit stations or nearby places, are approximate.",
          ],
        },
        {
          type: "p",
          text: "If you find inaccurate information or a suspicious listing, please report it using the details in section 18 so that we can investigate and correct it.",
        },
      ],
    },
    {
      key: "prohibited",
      title: "7. Prohibited Use",
      blocks: [
        { type: "p", text: "In using the website and service, you agree not to:" },
        {
          type: "ul",
          items: [
            "Publish listings for properties that do not exist, duplicate listings excessively, or quote untrue prices to attract enquiries.",
            "Use the website for fraud, money laundering or any other unlawful activity.",
            "Harvest user or listing data using automated means (scraping, bots, crawlers) without our written permission.",
            "Attempt to breach the system, probe for vulnerabilities, bypass authentication, or access data you are not entitled to.",
            "Upload viruses, malware or code capable of damaging the system.",
            "Send advertising or spam to other users.",
            "Reproduce, modify or commercially republish listing data on other channels without permission.",
            "Impersonate another person or falsely claim association with the Company.",
          ],
        },
        {
          type: "p",
          text: "Where these prohibitions are breached, the Company may suspend access immediately and take such legal action as it considers appropriate.",
        },
      ],
    },
    {
      key: "ip",
      title: "8. Intellectual Property",
      blocks: [
        {
          type: "p",
          text: `The ${c.brand} trade name, trade marks and logo, the page designs, source code, databases and content produced by the Company are the intellectual property of the Company or its licensors.`,
        },
        {
          type: "p",
          text: "You may use the website for personal purposes or for your work within the permissions granted to you. You may not reproduce, modify, distribute, sell or reverse-engineer any part of the service without our written permission.",
        },
      ],
    },
    {
      key: "third_party",
      title: "9. Third-Party Services and Links",
      blocks: [
        {
          type: "p",
          text: "The website may link to other sites or use third-party services such as maps, social media channels and authentication providers.",
        },
        {
          type: "p",
          text: "The content and operation of third-party services are outside our control. The Company is not liable for loss arising from their use, and we recommend that you also read those providers' own terms and policies.",
        },
      ],
    },
    {
      key: "fee",
      title: "10. Fees and Commission",
      blocks: [
        {
          type: "p",
          text: "Browsing listings on the website is free of charge. For paid services, such as listing promotion or brokerage, the terms and rates are communicated to you before the service begins.",
        },
        {
          type: "p",
          text: "Commission and other remuneration are governed by the agreement made in each case. The Company does not charge visitors a fee in exchange for contacting a property owner.",
        },
        {
          type: "note",
          text: "The Company never asks for deposits to be paid into a staff member's personal account. If someone uses our name to request a transfer, please verify with us using the details in section 18 before acting.",
        },
      ],
    },
    {
      key: "local",
      title: "11. Favourites and Data Stored on Your Device",
      blocks: [
        {
          type: "p",
          text: "The properties you mark as favourites, and some preferences such as your chosen language, are stored in your own browser and are not tied to a user account.",
        },
        {
          type: "p",
          text: "This data is lost if you clear your browser data, use private browsing, or change device. The Company cannot restore your favourites.",
        },
      ],
    },
    {
      key: "privacy",
      title: "12. Personal Data",
      blocks: [
        {
          type: "p",
          text: "The collection, use and disclosure of your personal data is governed by our Privacy Policy, prepared in accordance with the Thai Personal Data Protection Act B.E. 2562 (PDPA).",
        },
        { type: "link", href: "/policy-privacy", text: "Read the Privacy Policy" },
      ],
    },
    {
      key: "terminate",
      title: "13. Suspension and Termination",
      blocks: [
        { type: "p", text: "The Company may suspend or terminate your access, remove listings, or close your account where:" },
        {
          type: "ul",
          items: [
            "You breach these terms or applicable law.",
            "The information you provide is untrue or causes harm to others.",
            "Your engagement as a member of staff or agent of the Company ends.",
            "It is required by law or by order of a competent authority.",
          ],
        },
        {
          type: "p",
          text: "You may ask us to close your account at any time using the details in section 18. Provisions that by their nature survive termination — such as limitation of liability and intellectual property — remain in force.",
        },
      ],
    },
    {
      key: "liability",
      title: "14. Limitation of Liability",
      blocks: [
        {
          type: "p",
          text: "The website is provided on an \"as is\" basis. We make every reasonable effort to keep the service available and the information accurate, but we do not warrant that the website will be error-free or available without interruption.",
        },
        { type: "p", text: "To the extent permitted by law, the Company is not liable for loss arising from:" },
        {
          type: "ul",
          items: [
            "Your decision to buy, sell, rent or invest in reliance on information published on the website.",
            "Disputes, breaches of contract or acts of property owners, tenants, buyers or third parties.",
            "Service interruptions, data loss, or events of force majeure beyond the Company's control.",
            "Access to your account by another person as a result of your failure to keep your sign-in details confidential.",
          ],
        },
        {
          type: "p",
          text: "Nothing in these terms excludes liability that cannot be limited by law, such as liability arising from the Company's wilful misconduct or gross negligence.",
        },
      ],
    },
    {
      key: "indemnity",
      title: "15. Indemnity",
      blocks: [
        {
          type: "p",
          text: "If a third party brings a claim against the Company arising from content you submitted, your use of the service in breach of these terms, or your infringement of another person's rights, you agree to indemnify the Company against the resulting damages, fines and reasonable costs, including legal fees.",
        },
      ],
    },
    {
      key: "changes",
      title: "16. Changes to These Terms",
      blocks: [
        {
          type: "p",
          text: "The Company may update these terms from time to time. The revised version will be posted on this page with an updated revision date. Continued use of the website after publication constitutes acceptance of the revised terms.",
        },
        {
          type: "p",
          text: "For material changes affecting the rights of account holders, we will give advance notice by email or through an in-system notification.",
        },
      ],
    },
    {
      key: "law",
      title: "17. Governing Law and Disputes",
      blocks: [
        {
          type: "p",
          text: "These terms are governed by and construed in accordance with Thai law. Disputes arising out of or in connection with use of the website fall under the jurisdiction of the Thai courts.",
        },
        { type: "p", text: "Before commencing proceedings, we ask that you contact us so that we can try to resolve the matter amicably." },
        { type: "p", text: "If any provision is held void or unenforceable, the remaining provisions continue in full force." },
      ],
    },
    {
      key: "contact",
      title: "18. Contact Us",
      blocks: [
        { type: "p", text: "For questions about these terms, or to report a problem with the service, contact us at:" },
        {
          type: "ul",
          /* Channels the administrator has not filled in are omitted (see termsCompany) */
          items: [
            { label: "Service provider", text: c.name },
            { label: "Address", text: c.address },
            { label: "Email", text: c.email },
            { label: "Phone", text: c.phone },
            ...(c.hotline ? [{ label: "Hotline", text: c.hotline }] : []),
            ...(c.line_oa ? [{ label: "LINE", text: c.line_oa }] : []),
            { label: "Website", text: c.website },
          ],
        },
      ],
    },
  ],
});

/**
 * เนื้อหาข้อกำหนดฉบับที่พร้อมแสดงผล
 * @param {"TH"|"EN"} lang
 * @param {Object} company - ข้อมูลจาก services/company.service.js
 */
export const termsContent = (lang, company) => {
  const build = lang === "EN" ? EN : TH;
  return build(termsCompany(company, lang === "EN" ? "EN" : "TH"));
};
